import qs from 'query-string'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { Card } from 'antd'
import SlideNumber from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/SlideNumber'
import { StatusOverview } from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/ImDfmSlide/InactiveSlide'
import { ImDfmStateType } from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/ImDfmSlide/constants'
import {
    SLIDE_QUERY_PARAM,
    DFM_SOURCE_TYPE_3D,
    VIEWER_QUERY_PARAM,
    THREE_D_VIEWER_QUERY_PARAM,
} from '@Admin/AdminQuotingPage/constants'
import ThreeDTag from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/ImDfmSlide/ActiveSlide/ActiveSlideTitle/ThreeDTag'

import Box from '~/core/Box'
import Text from '~/core/Text'
import getColor from '~/lib/getColor'
import { useExtractUrlSearchParams } from '~/lib/extractUrlSearchParams'

import imDfmSlideAnnotationsQuery from './imDfmSlideAnnotationsQuery'
import styles from './styles.css'

const DEFAULT_IDX = '0'

interface ImDfmSlidePropsType {
    dfmFeedbackId: string
    idx: number
    key: string
    attachmentId?: string
    sourceType?: string | null
    status: ImDfmStateType | null
    topic?: string
    title: string
    isHighTouch: boolean
    isNonImDfmEnabled: boolean
    isImQuoteType: boolean
}

type ParamsType = {
    id: string
}

const ImDfmSlide = (props: ImDfmSlidePropsType) => {
    const {
        dfmFeedbackId,
        idx,
        attachmentId,
        topic,
        sourceType,
        status,
        title,
        isHighTouch,
        isNonImDfmEnabled,
        isImQuoteType,
    } = props

    const navigate = useNavigate()
    const { search } = useLocation()
    const { id } = useParams<ParamsType>()

    const [isActive, setIsActive] = useState<boolean>(false)

    const { data: slideAnnotationData } = useQuery(imDfmSlideAnnotationsQuery, {
        variables: { attachmentId, quoteModelRevisionId: id },
    })

    const hasAnnotations = Boolean(
        slideAnnotationData?.getQmrAttachmentAnnotations,
    )

    const searchParams = useExtractUrlSearchParams(search, [SLIDE_QUERY_PARAM])

    const activeSlideId = searchParams[SLIDE_QUERY_PARAM]

    const setActiveSlideQueryParam = useCallback(() => {
        navigate(
            {
                search: qs.stringify({
                    [SLIDE_QUERY_PARAM]: dfmFeedbackId,
                    [VIEWER_QUERY_PARAM]:
                        sourceType === DFM_SOURCE_TYPE_3D && !hasAnnotations
                            ? THREE_D_VIEWER_QUERY_PARAM
                            : undefined,
                }),
            },
            { replace: true },
        )
    }, [dfmFeedbackId, hasAnnotations, navigate, sourceType])

    useEffect(() => {
        if (!activeSlideId && idx.toString() === DEFAULT_IDX) {
            setActiveSlideQueryParam()
        }
    }, [activeSlideId, idx, setActiveSlideQueryParam])

    useEffect(() => {
        setIsActive(dfmFeedbackId === activeSlideId)
    }, [activeSlideId, dfmFeedbackId])

    return (
        <Box direction="row">
            <SlideNumber idx={idx} isLocatedInModal />
            <Card
                className={isActive ? styles.activeSlide : styles.inactiveSlide}
                data-test-target={`im-dfm-slide-${idx}`}
                onClick={setActiveSlideQueryParam}
                size="small"
                style={{
                    borderRadius: '4px',
                    marginBottom: '8px',
                    width: '100%',
                }}
            >
                <Box
                    borderBottom={`1px solid ${getColor('blackAlpha09')}`}
                    justifyContent="between"
                    marginBottom="8px"
                    paddingBottom="8px"
                >
                    <Text
                        color="blackAlpha65"
                        data-test-target={`slide-${idx}-topic`}
                        fontSize="14px"
                        fontWeight={500}
                    >
                        {!isImQuoteType && isNonImDfmEnabled && isHighTouch
                            ? title || 'No title specified'
                            : topic || `Slide ${idx + 1}`}
                    </Text>
                    {sourceType === DFM_SOURCE_TYPE_3D && (
                        <ThreeDTag isLocatedInModal />
                    )}
                </Box>
                <StatusOverview status={status} />
            </Card>
        </Box>
    )
}

export default ImDfmSlide
